/**
 * Several cards of one kind in a row, as one card.
 *
 * Counted by TARGET, not by call. Eleven edits to one document are one thing
 * that happened eleven times, so they draw as the ordinary single card with
 * "×11" beside the title. Listing the same title eleven times, which the first
 * version did, is the wall this exists to prevent.
 *
 * Several distinct targets get a header ("Edited 3 documents") and one row
 * each, with "×N" on any row that was hit more than once. Five rows, then
 * "and N more".
 */

import type { TranscriptMessage } from '@mod/core/model'
import { type ReactNode, useState } from 'react'
import { CardShell } from './CardShell'
import type { StopTrailing } from './stopVerdict'
import type { SubthreadState } from './subthreadState'
import { SubthreadChip, ThreadCard } from './ThreadCard'
import type { ThreadCard as Card, CardTarget } from './threadCards'
import type { ToolCall } from './threadRows'

const NOUN: Record<CardTarget, [string, string]> = {
  document: ['document', 'documents'],
  session: ['session', 'sessions'],
  comment: ['comment', 'comments'],
}

/** Rows shown before "and N more". */
export const GROUP_ROWS = 5

type Item = { card: Card; call?: ToolCall; message?: TranscriptMessage }

/** One entry per distinct target, in first-seen order, with how many times. */
export function distinctTargets(items: Item[]): (Item & { times: number })[] {
  const out: (Item & { times: number })[] = []
  const at = new Map<string, number>()
  for (const item of items) {
    const { card } = item
    const id = card.target === 'session' ? card.sessionId : card.documentId
    // No id (a create still pending, a comment) falls back to the label, so two
    // unrelated pending creates with different titles stay two rows.
    const key = `${card.target}:${id ?? `label:${card.label}`}`
    const seen = at.get(key)
    if (seen === undefined) {
      at.set(key, out.length)
      out.push({ ...item, times: 1 })
    } else {
      const entry = out[seen]
      entry.times += 1
      // The latest call wins, so a pending card that later resolved is shown
      // resolved.
      out[seen] = { ...entry, card, ...(item.call ? { call: item.call } : {}) }
    }
  }
  return out
}

export function ThreadCardGroup({
  items,
  onOpenDocument,
  onOpenSession,
  resolveTitle,
  resolveSessionState,
  renderUndo,
  stopTrailing,
}: {
  items: Item[]
  onOpenDocument?: (documentId: string) => void
  onOpenSession?: (sessionId: string) => void
  resolveTitle?: (target: CardTarget, id: string) => string | undefined
  /** RFC 0302 S5: what the subthread a session row points at is doing. */
  resolveSessionState?: (sessionId: string) => SubthreadState | undefined
  /** RFC 0301 S9. A row hit several times undoes its LATEST edit, the one the
   *  row already shows. */
  renderUndo?: (documentId: string, hash: string) => ReactNode
  /** RFC 0348 3e: what the World agent decided, beside each report. */
  stopTrailing?: StopTrailing
}) {
  const [all, setAll] = useState(false)
  const [expanded, setExpanded] = useState(false)
  // RFC 0348 3e: a burst of reports is one line that opens on click, each
  // agent a row inside with its verdict. Every other group is a list that is
  // always open.
  const reports = items.every((i) => i.message?.kind === 'session_stopped')
  const trailingOf = (item: Item) =>
    item.message && stopTrailing ? stopTrailing(item.message) : undefined
  const distinct = distinctTargets(items)

  // All the same thing: the single card, with a count.
  if (distinct.length === 1) {
    const only = distinct[0]
    const trailing = trailingOf(only)
    return (
      <ThreadCard
        card={only.card}
        {...(trailing ? { trailing } : {})}
        {...(only.call ? { call: only.call } : {})}
        times={only.times}
        {...(onOpenDocument ? { onOpenDocument } : {})}
        {...(onOpenSession ? { onOpenSession } : {})}
        {...(resolveTitle ? { resolveTitle } : {})}
        {...(resolveSessionState ? { resolveSessionState } : {})}
        {...(renderUndo ? { renderUndo } : {})}
      />
    )
  }

  const first = distinct[0].card
  const [, many] = NOUN[first.target]
  const pending = distinct.some((i) => i.card.pending)
  const shown = all ? distinct : distinct.slice(0, GROUP_ROWS)
  const hidden = distinct.length - shown.length

  return (
    <CardShell
      verb={reports ? 'Heard from' : first.verb}
      label={`${distinct.length} ${many}`}
      pending={pending}
      always={!reports}
      // The whole line opens a burst, not only its chevron.
      {...(reports
        ? {
            open: expanded,
            onToggle: setExpanded,
            onOpen: () => setExpanded((v) => !v),
          }
        : {})}
      body={
        <div className="py-0.5">
          {shown.map((item, i) => {
            const { card, call, times } = item
            const id =
              card.target === 'session' ? card.sessionId : card.documentId
            const label =
              card.target === 'comment'
                ? card.label
                : ((id ? resolveTitle?.(card.target, id) : undefined) ??
                  card.label)
            const open =
              card.pending || !id
                ? undefined
                : card.target === 'session'
                  ? onOpenSession && (() => onOpenSession(id))
                  : onOpenDocument && (() => onOpenDocument(id))
            return (
              <div
                key={call?.id ?? item.message?.entryId ?? String(i)}
                className="flex items-baseline gap-1.5 py-1 pr-2.5 pl-2.5"
              >
                {open ? (
                  <button
                    type="button"
                    onClick={open}
                    title={label}
                    className="min-w-0 flex-1 truncate text-left text-[0.8rem] text-foreground hover:underline"
                  >
                    {label}
                  </button>
                ) : (
                  <span
                    title={label}
                    className="min-w-0 flex-1 truncate text-[0.8rem] text-foreground/80"
                  >
                    {label}
                  </span>
                )}
                {times > 1 ? (
                  <span className="shrink-0 text-[0.75rem] text-muted-foreground tabular-nums">
                    ×{times}
                  </span>
                ) : null}
                {/* RFC 0302 S5: several branches under one post are one card
                    with a row each, and each row still says what its own
                    subthread is doing. */}
                {reports && card.verb !== 'Finished' ? (
                  <span className="shrink-0 text-[0.75rem] text-muted-foreground">
                    {card.verb.toLowerCase()}
                  </span>
                ) : null}
                {trailingOf(item) ??
                  (card.target === 'session' && id
                    ? (() => {
                        const state = resolveSessionState?.(id)
                        return state ? <SubthreadChip state={state} /> : null
                      })()
                    : null)}
                {card.documentId && card.changeHash
                  ? renderUndo?.(card.documentId, card.changeHash)
                  : null}
              </div>
            )
          })}
          {hidden > 0 ? (
            <button
              type="button"
              onClick={() => setAll(true)}
              className="py-1 pr-2.5 pl-2.5 text-left text-[0.75rem] text-muted-foreground hover:text-foreground"
            >
              and {hidden} more
            </button>
          ) : null}
        </div>
      }
    />
  )
}
